/**
 * Просмотр вложения во весь экран.
 *
 * ── Зачем отдельный слой ────────────────────────────────────────────────────
 *
 * В ленте вложение показывается маленьким: иначе одно фото занимает экран и
 * переписка перестаёт читаться. Но рабочая переписка — это документы, чеки и
 * скриншоты, где важна деталь, а не факт наличия картинки. Без просмотра
 * менеджеру приходилось бы скачивать файл, чтобы прочитать номер на фото.
 *
 * ── Почему не новая вкладка ─────────────────────────────────────────────────
 *
 * Открытие в отдельной вкладке дало бы прямую ссылку на файл, а вместе с ней
 * — адрес, который живёт дольше сессии и уходит в историю браузера и в чужой
 * буфер обмена. Слой поверх приложения не создаёт такой ссылки.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { TelegramMediaDto } from '@tw/shared-types';
import { XIcon } from './icons.tsx';

interface MediaViewerProps {
  media: TelegramMediaDto;
  src: string;
  onClose: () => void;
}

export function MediaViewer({ media, src, onClose }: MediaViewerProps) {
  /*
   * Escape закрывает, и это единственный способ, который человек ищет не
   * глядя. Слушатель висит на документе, а не на слое: фокус после открытия
   * может оказаться где угодно, в том числе внутри <video>.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const video = media.kind === 'video' || media.kind === 'videoNote';

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Просмотр вложения"
      // Клик по фону закрывает, клик по самому вложению — нет: иначе пауза
      // в видео закрывала бы просмотр.
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-6 backdrop-blur-sm"
    >
      <button
        type="button"
        aria-label="Закрыть"
        onClick={onClose}
        className="absolute right-4 top-4 grid size-9 place-items-center rounded-full border-transparent bg-white/10 text-white transition-colors hover:bg-white/20"
      >
        <XIcon className="size-5" />
      </button>

      {video ? (
        <video
          src={src}
          controls
          autoPlay
          onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full rounded-xl"
        />
      ) : (
        <img
          src={src}
          alt="Вложение"
          onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full rounded-xl object-contain"
        />
      )}
    </div>,
    document.body,
  );
}
